import * as THREE from "three";
import {
  acceleratedRaycast,
  computeBoundsTree,
  disposeBoundsTree,
} from "three-mesh-bvh";

THREE.Mesh.prototype.raycast = acceleratedRaycast;

// single global ref other files can import/use
export const guidePaintRef = { current: null };

export class GuidePaint {
  constructor({ size = 2048, color = "rgba(255,0,0,1)", brush = 2 } = {}) {
    // detect mobile/tablet (iOS especially)
    this._isMobile =
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 1 &&
        Math.min(screen.width, screen.height) < 900);

    

    // 👇 this is the BIG win
    const desired = size;
    const mobileSize = 1024; // try 512 first; if still laggy, use 384 or 256
    size = this._isMobile ? Math.min(desired, mobileSize) : desired;

    // --- config ---
    this.size = size;
    this.alpha = 0.5;
    this.color = color;

    // rope stabilizer
    this.rope = {
      enabled: true,
      strength: 0.6, // 0..1 (higher = smoother, more lag)
      minDist: 0.6, // px (ignore micro jitter)
    };
    try {
      const v = localStorage.getItem("tt_rope_enabled");
      if (v !== null) this.rope.enabled = v === "1";
    } catch {}
    this._ropePt = null; // last smoothed point
    this._rawPt = null; // last raw point

    try {
      window.dispatchEvent(
        new CustomEvent("guidepaint:color", { detail: { color: this.color } })
      );
    } catch {}
    this.brush = brush;
    // in constructor, after: this.brush = brush;
    try {
      const saved = parseInt(localStorage.getItem("tt_brush_px") || "", 10);
      if (Number.isFinite(saved)) {
        this.brush = Math.max(1, Math.min(48, saved));
      }
    } catch {}
    this._onPreDown = this._onPreDown.bind(this);
    this._autoDisable = true; // auto turn off after stroke ends (default true)

    // painting state
    this.enabled = false;
    this._dirty = false;
    this.autoLayerOnStop = false;
    this.mode = "paint"; // "paint" | "erase"

    this._pendingHit = null;
    this._moveRaf = 0;
    this._tickMove = this._tickMove.bind(this);

    // symmetry
    this.symmetry = false;

    // painter canvas
    this.canvas = document.createElement("canvas");
    this.canvas.width = this.canvas.height = size;
    this.ctx = this.canvas.getContext("2d", {
      willReadFrequently: true,
      alpha: true,
    });
    this.ctx.clearRect(0, 0, size, size);

    // history
    this.undoStack = [];
    this.redoStack = [];
    this.maxHistory = 20;

    // calibration + atlas sub-rect
    this.calib = { uScale: 1, vScale: 1, uBias: 0, vBias: 0 };
    this.uvRect = { x: 0, y: 0, w: 1, h: 1 };
    this.FLIP_V = false;

    // live texture
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.flipY = false;
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.generateMipmaps = false;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.texture.wrapT = THREE.ClampToEdgeWrapping;

    // raycast helpers
    this.raycaster = new THREE.Raycaster();
    this.raycaster.params.Mesh = { side: THREE.DoubleSide };
    this.pointer = new THREE.Vector2();
    this._normalMatrix = new THREE.Matrix3();
    this._viewDir = new THREE.Vector3();

    this.raycaster.firstHitOnly = true; // if supported (depends on three version)

    // external controls (wired from PreviewPane)
    this.orbit = null;

    // targets
    this.meshes = [];
    this.camera = null;
    this.canvasEl = null;

    // stroke state
    this._dragging = false;
    this._lastHit = null;
    this._lastHitMirror = null;
    this._penActive = false; // Apple Pencil down/up

    this._activeMesh = null; // 👈 lock mesh during stroke

    // hold state (thumb pads / keyboard)
    this._holdPaint = false;
    this._holdErase = false;

    // latch window so painting stays enabled briefly after pad press
    this._padArmedUntil = 0;

    // bindings for event handlers
    this._onDown = this._onDown.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this._onKey = this._onKey.bind(this);

    // bind public helpers (so "this" is never lost)
    this.pressPaint = this.pressPaint.bind(this);
    this.releasePaint = this.releasePaint.bind(this);
    this.pressErase = this.pressErase.bind(this);
    this.releaseErase = this.releaseErase.bind(this);
    this.armPadHold = this.armPadHold.bind(this);

    // --- bind RAF + handlers so `this` is always the GuidePaint instance ---
    this._tickMove = this._tickMove.bind(this);
    this._onDown = this._onDown.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this._onKey = this._onKey.bind(this);
  }

  setBrush(px) {
    const clamped = Math.max(1, Math.min(48, px | 0));
    this.brush = clamped;
    try {
      localStorage.setItem("tt_brush_px", String(clamped));
    } catch {}
    try {
      window.dispatchEvent(
        new CustomEvent("guidepaint:brush", { detail: { size: clamped } })
      );
    } catch {}
  }
  getBrush() {
    return this.brush | 0;
  }

  setRopeEnabled(on) {
    this.rope.enabled = !!on;
    try {
      localStorage.setItem("tt_rope_enabled", this.rope.enabled ? "1" : "0");
    } catch {}
  }

  isRopeEnabled() {
    return !!this.rope?.enabled;
  }

  _ropeFilter(px, py) {
    // ✅ OFF = passthrough (no smoothing)
    if (!this.rope?.enabled) {
      // keep raw tracking in sync so enabling mid-stroke doesn't jump
      this._rawPt = { x: px, y: py };
      this._ropePt = { x: px, y: py };
      return { x: px, y: py };
    }

    // first point: snap
    if (!this._ropePt) {
      this._ropePt = { x: px, y: py };
      this._rawPt = { x: px, y: py };
      return { x: px, y: py };
    }

    const dx = px - this._rawPt.x;
    const dy = py - this._rawPt.y;
    const dist = Math.hypot(dx, dy);

    // ignore ultra tiny movement (noise)
    const minDist = Number(this.rope.minDist ?? 0);
    if (dist < minDist) {
      return { x: this._ropePt.x, y: this._ropePt.y };
    }

    this._rawPt.x = px;
    this._rawPt.y = py;

    // exponential smoothing (rope feel)
    const k = Math.max(0, Math.min(1, Number(this.rope.strength ?? 0.6)));
    this._ropePt.x = this._ropePt.x + (px - this._ropePt.x) * (1 - k);
    this._ropePt.y = this._ropePt.y + (py - this._ropePt.y) * (1 - k);

    return { x: this._ropePt.x, y: this._ropePt.y };
  }

  isEnabled() {
    return !!this.enabled;
  }

  // --- tiny rAF pump so the texture updates smoothly while dragging ---
  // --- NO PUMP ---
  // Updating CanvasTexture every rAF is expensive on iPad/phone.
  // We only update via _commitStrokeTick (throttled) now.
  _startPump() {}
  _stopPump() {}

  /** Public: keep painting enabled for N ms after pad press */
  armPadHold(ms = 2000) {
    this._padArmedUntil = Date.now() + ms;
  }

  isPenActive() {
    return !!this._penActive;
  }

  /** Return the internal painting canvas so we can bake it into a layer */
  getCanvas() {
    return this.canvas || null;
  }

  /* ========== public small helpers for UI ========== */
  canUndo() {
    return this.undoStack.length > 0;
  }
  canRedo() {
    return this.redoStack.length > 0;
  }
  setSymmetry(on) {
    this.symmetry = !!on;
  }
  hasStrokes() {
    return !!this._dirty;
  }
  setActive(on) {
    this.setEnabled(on);
  }

  /**
   * Flush any in-progress stroke and, if autoLayerOnStop is enabled,
   * emit a "guidepaint:auto-layer" so the app can bake this canvas
   * into the actual project layers before a snapshot.
   */
  flush() {
    // If a stroke is still mid-drag, end it cleanly
    if (this._dragging) {
      this._dragging = false;

      try {
        this.canvasEl?.dispatchEvent(
          new CustomEvent("guidepaint:drag-end", { bubbles: true })
        );
      } catch {}

      this._emitHistory();
    }

    this._commitStrokeTick(true);

    // If there are strokes on this canvas and we're supposed to auto-layer,
    // fire the same style of event that setEnabled(false) would cause.
    if (this._dirty && this.autoLayerOnStop) {
      try {
        window.dispatchEvent(
          new CustomEvent("guidepaint:auto-layer", {
            detail: { canvas: this.canvas },
          })
        );
      } catch {}

      // Mark as committed so repeated flush calls don't re-fire
      this._dirty = false;
    }
  }

  _emitHistory() {
    try {
      window.dispatchEvent(
        new CustomEvent("guidepaint:history", {
          detail: { canUndo: this.canUndo(), canRedo: this.canRedo() },
        })
      );
    } catch {}
  }

  // When enabled and no hold is active, use current mode (paint/erase)
  _currentMode() {
    if (!this.enabled) return null;
    if (this._holdErase) return "erase";
    if (this._holdPaint) return "paint";
    return this.mode;
  }

  // -------- orbit wiring ----------
  setOrbitControls(controls) {
    this.orbit = controls || null;
  }

  // ---------------- wiring ----------------
  setTargets(meshes, camera, canvasEl) {
    this._removeListeners();

    // ✅ Always store REAL meshes (not Groups)
    const flat = [];
    for (const m of meshes || []) {
      if (!m) continue;

      if (m.isMesh || m.isSkinnedMesh) {
        flat.push(m);
        continue;
      }

      if (typeof m.traverse === "function") {
        m.traverse((o) => {
          if (o?.isMesh || o?.isSkinnedMesh) flat.push(o);
        });
      }
    }

    // ✅ Safety: make sure bounds exist (raycast can behave weirdly without them)
    for (const o of flat) {
      const g = o.geometry;
      if (!g) continue;
      if (!g.boundingBox) g.computeBoundingBox();
      if (!g.boundingSphere) g.computeBoundingSphere();
    }

    this.meshes = flat;
    this.camera = camera || null;
    this.canvasEl = canvasEl || null;

    if (window.__TT_GP_DEBUG) {
      console.log(
        "[gp] setTargets flat:",
        flat.map((o) => o.name),
        "count:",
        flat.length
      );
    }

    this._installListeners();
  }

  // Capture-phase "pre-down" (helps Apple Pencil + pad latch)
  _onPreDown(e) {
    // If pad was recently pressed, treat Pencil-down like an active hold
    if (e.pointerType === "pen" && Date.now() < this._padArmedUntil) {
      this._penActive = true;
      this._holdPaint = true;
      this._holdErase = false;
      this.setEnabled(true); // ensure canvas accepts this very pen down
    }
  }

  _installListeners() {
    this._removeListeners();
    if (!this.canvasEl) return;

    // capture-phase pointerdown on window
    window.addEventListener("pointerdown", this._onPreDown, {
      capture: true,
      passive: false,
    });

    this._onDownBound = this._onDown;
    this._onMoveBound = this._onMove;
    this._onUpBound = this._onUp;
    this._onKeyBound = this._onKey;

    this.canvasEl.addEventListener("pointerdown", this._onDownBound, {
      passive: false,
    });
    window.addEventListener("pointermove", this._onMoveBound, {
      passive: false,
    });
    window.addEventListener("pointerup", this._onUpBound, { passive: false });
    window.addEventListener("pointercancel", this._onUpBound, {
      passive: false,
    });
    window.addEventListener("keydown", this._onKeyBound, { passive: false });
    window.addEventListener("keyup", this._onKeyBound, { passive: false });
  }

  _removeListeners() {
    if (this.canvasEl) {
      this.canvasEl.removeEventListener(
        "pointerdown",
        this._onDownBound || this._onDown
      );
    }
    window.removeEventListener(
      "pointermove",
      this._onMoveBound || this._onMove
    );
    window.removeEventListener("pointerup", this._onUpBound || this._onUp);
    window.removeEventListener("pointercancel", this._onUpBound || this._onUp);
    window.removeEventListener("keydown", this._onKeyBound || this._onKey);
    window.removeEventListener("keyup", this._onKeyBound || this._onKey);
    // remove capture-phase listener — must pass the same capture flag (true)
    window.removeEventListener("pointerdown", this._onPreDown, true);
  }

  detach() {
    this._removeListeners();
    this.meshes = [];
    this.camera = null;
    this.canvasEl = null;
  }

  /* === public press/hold helpers for UI pads === */
  pressPaint() {
    this._autoDisable = true;
    this._holdPaint = true;
    this._holdErase = false;
    this.setEnabled(true);
  }
  releasePaint() {
    this._holdPaint = false;
    this.setEnabled(false); // always stop painting, re-enable orbit
  }

  pressErase() {
    this._holdErase = true;
    this._holdPaint = false;
    this.setEnabled(true);
  }
  releaseErase() {
    this._holdErase = false;
    this.setEnabled(false); // always stop painting, re-enable orbit
  }

  // ---------------- controls ----------------
  setEnabled(on) {
    const prev = this.enabled;
    this.enabled = !!on;

    
    try {
      if (this.canvasEl) {
        this.canvasEl.style.touchAction = this.enabled ? "none" : "auto";
        this.canvasEl.style.pointerEvents = "auto";
      }
    } catch {}

    try {
      window.dispatchEvent(
        new CustomEvent("guidepaint:toggle", { detail: { on: this.enabled } })
      );
    } catch {}

    if (prev && !this.enabled && this._dirty && this.autoLayerOnStop) {
      try {
        window.dispatchEvent(
          new CustomEvent("guidepaint:auto-layer", {
            detail: { canvas: this.canvas },
          })
        );
      } catch {}
    }
  }

  setColor(rgbaCss) {
    this.color = rgbaCss;
    try {
      window.dispatchEvent(
        new CustomEvent("guidepaint:color", { detail: { color: rgbaCss } })
      );
    } catch {}
  }

  setMode(mode) {
    this.mode = mode === "erase" ? "erase" : "paint";
  }
  toggleMode() {
    this.setMode(this.mode === "paint" ? "erase" : "paint");
  }
  setCalibration({ uScale = 1, vScale = 1, uBias = 0, vBias = 0 } = {}) {
    this.calib.uScale = +uScale || 1;
    this.calib.vScale = +vScale || 1;
    this.calib.uBias = +uBias || 0;
    this.calib.vBias = +vBias || 0;
  }
  setUvRect(rect = { x: 0, y: 0, w: 1, h: 1 }) {
    const x = Number(rect.x ?? 0);
    const y = Number(rect.y ?? 0);
    const w = Number(rect.w ?? 1);
    const h = Number(rect.h ?? 1);
    this.uvRect = {
      x: isFinite(x) ? x : 0,
      y: isFinite(y) ? y : 0,
      w: Math.max(1e-6, isFinite(w) ? w : 1),
      h: Math.max(1e-6, isFinite(h) ? h : 1),
    };
  }

  clear() {
    this._pushUndo();
    this.ctx.clearRect(0, 0, this.size, this.size);
    this.texture.needsUpdate = true;
    try {
      window.dispatchEvent(new Event("guidepaint:stroke"));
    } catch {}
    this._dirty = false;
    try {
      window.dispatchEvent(new Event("guidepaint:stroke"));
    } catch {}
    this._emitHistory();
  }

  getTexture() {
    return this.texture;
  }
  isEmpty() {
    return !this._dirty;
  }
  async toObjectURL(type = "image/png", quality = 1) {
    return await new Promise((res) =>
      this.canvas.toBlob(
        (b) => res(b ? URL.createObjectURL(b) : ""),
        type,
        quality
      )
    );
  }

  /* ========== undo / redo core ========== */
  _snapshot() {
    try {
      return this.ctx.getImageData(0, 0, this.size, this.size);
    } catch {
      return null;
    }
  }
  _pushUndo() {
    const snap = this._snapshot();
    if (!snap) return;
    this.undoStack.push(snap);
    if (this.undoStack.length > this.maxHistory) this.undoStack.shift();
  }
  _applyImageData(snap) {
    this.ctx.putImageData(snap, 0, 0);
    this.texture.needsUpdate = true;
    try {
      window.dispatchEvent(new Event("guidepaint:stroke"));
    } catch {}
  }
  undo() {
    if (this.undoStack.length === 0) return false;
    const cur = this._snapshot();
    if (cur) this.redoStack.push(cur);
    const snap = this.undoStack.pop();
    this._applyImageData(snap);
    this._emitHistory();
    return true;
  }
  redo() {
    if (this.redoStack.length === 0) return false;
    const cur = this._snapshot();
    if (cur) this.undoStack.push(cur);
    const snap = this.redoStack.pop();
    this._applyImageData(snap);
    this._emitHistory();
    return true;
  }

  /* ========== pointer / raycast ========== */
  _updatePointerFromEvent(e) {
    if (!this.canvasEl) return;
    const rect = this.canvasEl.getBoundingClientRect();
    const w = Math.max(1, rect.width);
    const h = Math.max(1, rect.height);
    const x = (e.clientX - rect.left) / w;
    const y = (e.clientY - rect.top) / h;
    this.pointer.set(x * 2 - 1, -(y * 2 - 1));
  }
  _updatePointerFromClientXY(clientX, clientY) {
    const el = this.canvasEl;
    if (!el) return;

    // ✅ MUST be the WebGL canvas element rect (not a wrapper div)
    const r = el.getBoundingClientRect();

    // guard
    if (!r.width || !r.height) return;

    // normalize to 0..1 inside the canvas
    const nx = (clientX - r.left) / r.width;
    const ny = (clientY - r.top) / r.height;

    // clamp so edges still work (avoid >1 or <0 when finger drifts)
    const x = Math.max(0, Math.min(1, nx));
    const y = Math.max(0, Math.min(1, ny));

    // convert to NDC for three raycaster
    this.pointer.x = x * 2 - 1;
    this.pointer.y = -(y * 2 - 1);
    if (window.__TT_GP_DEBUG) {
      console.log("[GP ptr]", {
        clientX,
        clientY,
        rect: { top: r.top, left: r.left, w: r.width, h: r.height },
        ndc: { x: this.pointer.x, y: this.pointer.y },
      });
    }
  }

  _intersectUV(meshOverride = null) {
    if (!this.camera || !this.meshes?.length) return null;

    // lazy init for stability
    if (!this._lastHit) this._lastHit = null;

    this.raycaster.setFromCamera(this.pointer, this.camera);
    const targets = meshOverride ? [meshOverride] : this.meshes;

    const hits = this.raycaster.intersectObjects(targets, true);
    const MAX_WORLD_JUMP = 0.08; // start here; tweak 0.12..0.30
    if (window.__TT_GP_DEBUG) {
      console.log(
        "[GP hits]",
        hits.length,
        targets.map((t) => t?.name)
      );
    }
    if (!hits.length) {
      if (window.__TT_GP_DEBUG)console.log("[GP] no ray hits", {
        meshes: targets?.length,
        ptr: { ...this.pointer },
        uvRect: this.uvRect,
        calib: this.calib,
      });
      return null;
    }

    // ---- tuning knobs ----
    const MIN_FACING_STRICT = 0.06; // prefer front-facing
    const MIN_FACING_LOOSE = -0.25; // allow grazing only if strict finds nothing
    const STICK_EPS = 0.008;
    const DIST_BIAS = 0.0005;
    // ----------------------

    const WRAP = (d) => ((((d + 0.5) % 1) + 1) % 1) - 0.5;

    // tune (start conservative)
    const MAX_UV_JUMP_U = 0.1;
    const MAX_UV_JUMP_V = 0.16;
    const SEAM_WORLD_EPS = 0.03; // adjust if needed

    let best = null;
    let bestLoose = null;

    // view direction (from surface toward camera)
    this._viewDir.copy(this.raycaster.ray.direction).negate().normalize();
    this._normalMatrix.identity();

    for (const h of hits) {
      if (!h.uv || !h.face) continue;

      // world-space normal
      const n = h.face.normal.clone();
      this._normalMatrix.getNormalMatrix(h.object.matrixWorld);
      n.applyNormalMatrix(this._normalMatrix).normalize();

      const facing = n.dot(this._viewDir); // -1..1
      if (facing < MIN_FACING_LOOSE) continue;

      // ✅ WORLD-SPACE continuity reject (prevents “flip to other side of limb”)
      if (this._lastHitPoint && h.point) {
        const worldDist = h.point.distanceTo(this._lastHitPoint);
        if (worldDist > MAX_WORLD_JUMP) {
          if (window.__TT_GP_DEBUG) {
            console.log("[GP] reject far hit", {
              obj: h.object?.name,
              worldDist: +worldDist.toFixed(3),
              max: MAX_WORLD_JUMP,
              rayDist: +h.distance.toFixed(3),
            });
          }
          continue;
        }
      }

      // ✅ compute uRel/vRel PER HIT so we can reject bad candidates
      const { x: rx, y: ry, w: rw, h: rh } = this.uvRect;
      let u = h.uv.x;
      let v = h.uv.y;
      if (this.FLIP_V) v = 1 - v;

      let uRel = (u - rx) / rw;
      let vRel = (v - ry) / rh;
      uRel = uRel * this.calib.uScale + this.calib.uBias;
      vRel = vRel * this.calib.vScale + this.calib.vBias;

      // reject out-of-rect
      if (uRel < 0 || uRel > 1 || vRel < 0 || vRel > 1) continue;

      // ✅ seam-jump guard (this is the new part)
      if (this._lastUV && this._lastHitPoint) {
        const du = Math.abs(WRAP(uRel - this._lastUV.u));
        const dv = Math.abs(vRel - this._lastUV.v);

        const worldDist = h.point.distanceTo(this._lastHitPoint);

        const uvJump = du > MAX_UV_JUMP_U || dv > MAX_UV_JUMP_V;

        // Reject only if it's a UV jump AND it also moved far in world space.
        // If worldDist is small, it's likely a seam crossing → allow it.
        if (uvJump && worldDist > SEAM_WORLD_EPS) {
          continue;
        }
      }

      // score
      const score =
        1 / (h.distance + 1e-6) + facing * 0.05 - h.distance * DIST_BIAS;

      // hysteresis / stickiness
      let s = score;
      if (this._lastRayHit?.object && h.object === this._lastRayHit.object) {
        if (!best || h.distance <= best.distance + STICK_EPS) s += 0.08;
      }

      // cache uRel/vRel on the hit so we can return them later
      h._uRel = uRel;
      h._vRel = vRel;

      if (facing >= MIN_FACING_STRICT) {
        if (!best || s > best._score) {
          best = h;
          best._score = s;
        }
      } else {
        const looseScore = s * 0.9;
        if (!bestLoose || looseScore > bestLoose._score) {
          bestLoose = h;
          bestLoose._score = looseScore;
        }
      }
    }

    const chosen = best || bestLoose;
    if (!chosen) return null;

    // pull cached rel UVs
    const uRel = chosen._uRel;
    const vRel = chosen._vRel;

    // remember for stickiness + jump guard

    this._lastUV = { u: uRel, v: vRel };

    this._lastRayHit = { object: chosen.object, distance: chosen.distance };
    // ✅ update continuity anchor for next pick
    if (chosen.point) this._lastHitPoint = chosen.point.clone();

    // optional: keep last UV too (useful if you later add UV jump logic)
    this._lastUV = { u: uRel, v: vRel };
    this._lastHitPoint = chosen.point
      ? chosen.point.clone()
      : this._lastHitPoint;

    return {
      u: uRel,
      v: vRel,
      x: uRel * this.size,
      y: vRel * this.size,
      hit: chosen,
    };
  }

  // ✅ PUBLIC: allow other UI (PreviewPane) to reuse our robust UV raycast
  pickUVAtClientXY(clientX, clientY, meshOverride = null) {
    try {
      this._updatePointerFromClientXY(clientX, clientY);
      return this._intersectUV(meshOverride);
    } catch (e) {
      console.warn("[GuidePaint] pickUVAtClientXY failed", e);
      return null;
    }
  }

  /* ========== symmetry helpers ========== */
  _symCenterXpx() {
    const { x: rx, w: rw } = this.uvRect;
    return this.size * (rx + rw * 0.5);
  }
  _mirrorPx(px) {
    const cx = this._symCenterXpx();
    return cx * 2 - px;
  }
  _mirrorUV(uv) {
    const { x: rx, w: rw } = this.uvRect;
    const mirroredU = rx + rw - (uv.u - rx);
    return {
      u: mirroredU,
      v: uv.v,
      x: mirroredU * this.size,
      y: uv.v * this.size,
    };
  }

  /* ========== drawing helpers ========== */
  _line(x1, y1, x2, y2) {
    const m = this._currentMode();
    if (!m) return;
    this.ctx.globalCompositeOperation =
      m === "erase" ? "destination-out" : "source-over";
    this.ctx.globalAlpha = 1;
    if (m === "paint") this.ctx.strokeStyle = this.color;

    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();
  }

  _lineSym(x1, y1, x2, y2) {
    this._line(x1, y1, x2, y2);
    if (this.symmetry) {
      const mx1 = this._mirrorPx(x1);
      const mx2 = this._mirrorPx(x2);
      this._line(mx1, y1, mx2, y2);
    }
  }

  _dabOne(x, y) {
    const m = this._currentMode();
    if (!m) return;

    const r = this.brush * 0.5;

    this.ctx.globalCompositeOperation =
      m === "erase" ? "destination-out" : "source-over";
    this.ctx.globalAlpha = 1;
    if (m === "paint") this.ctx.fillStyle = this.color;

    this.ctx.beginPath();
    this.ctx.arc(x, y, r, 0, Math.PI * 2);
    this.ctx.fill();
  }

  _dab(x, y) {
    const W = this.size,
      H = this.size;
    const r = this.brush * 0.5;

    // main
    this._dabOne(x, y);

    // wrap edges (keeps continuity)
    if (x < r) this._dabOne(x + W, y);
    if (x > W - r) this._dabOne(x - W, y);
    if (y < r) this._dabOne(x, y + H);
    if (y > H - r) this._dabOne(x, y - H);

    // symmetry
    if (this.symmetry) {
      const mx = this._mirrorPx(x);
      this._dabOne(mx, y);
      if (mx < r) this._dabOne(mx + W, y);
      if (mx > W - r) this._dabOne(mx - W, y);
      if (y < r) this._dabOne(mx, y + H);
      if (y > H - r) this._dabOne(mx, y - H);
    }
  }

  _dot(px, py) {
    this._dab(px, py);
    this._commitStrokeTick(true); // force one update at stroke start/taps
  }

  // ---------- seam helpers (UV-island twins) ----------

  // Build a mapping: faceIndex -> [twinFaceIndex, ...]
  // Faces are considered "twins" if they share the same 3 vertex POSITIONS
  // (common when UVs split islands: positions duplicate, UVs differ)
  _ensureSeamTwinsForMesh(mesh) {
    if (!mesh?.geometry) return;

    const geom = mesh.geometry;
    const gid = geom.uuid;

    // cache per-geometry
    if (this._seamTwinCache?.gid === gid) return;
    if (!this._seamTwinCache) this._seamTwinCache = {};

    const pos = geom.attributes?.position;
    const uv = geom.attributes?.uv;
    if (!pos || !uv) {
      this._seamTwinCache = { gid, twins: null };
      return;
    }

    // index optional
    const index = geom.index ? geom.index.array : null;

    // quantize positions so float noise doesn't break keys
    const Q = 1e-5;
    const q = (v) => Math.round(v / Q);

    const triCount = index ? index.length / 3 : pos.count / 3;

    // key: sorted vertex position triplets
    const bucket = new Map(); // key -> [faceIndex...]
    const keyParts = new Array(3);

    const getIdx = (faceIndex, k) => {
      if (index) return index[faceIndex * 3 + k];
      return faceIndex * 3 + k;
    };

    const readPos = (i) => {
      const x = pos.getX(i),
        y = pos.getY(i),
        z = pos.getZ(i);
      return `${q(x)},${q(y)},${q(z)}`;
    };

    for (let f = 0; f < triCount; f++) {
      // gather 3 verts
      for (let k = 0; k < 3; k++) {
        const vi = getIdx(f, k);
        keyParts[k] = readPos(vi);
      }
      // sort so winding doesn't matter
      keyParts.sort();
      const key = keyParts.join("|");

      const arr = bucket.get(key);
      if (arr) arr.push(f);
      else bucket.set(key, [f]);
    }

    // build twins table
    const twins = new Array(triCount);
    for (let f = 0; f < triCount; f++) twins[f] = [];

    for (const faces of bucket.values()) {
      if (faces.length < 2) continue; // no seam duplicates
      for (let i = 0; i < faces.length; i++) {
        const a = faces[i];
        for (let j = 0; j < faces.length; j++) {
          if (i === j) continue;
          twins[a].push(faces[j]);
        }
      }
    }

    this._seamTwinCache = { gid, twins, pos, uv, index };
  }

  _getFaceVertexIndices(faceIndex) {
    const { index, pos } = this._seamTwinCache || {};
    if (!pos) return null;

    if (index) {
      const i0 = index[faceIndex * 3 + 0];
      const i1 = index[faceIndex * 3 + 1];
      const i2 = index[faceIndex * 3 + 2];
      return [i0, i1, i2];
    } else {
      // non-indexed: triangles are consecutive
      const i0 = faceIndex * 3 + 0;
      const i1 = faceIndex * 3 + 1;
      const i2 = faceIndex * 3 + 2;
      return [i0, i1, i2];
    }
  }

  // Compute barycentric weights for point P (in local space) on triangle (a,b,c) (local space)
  _barycentric(P, A, B, C) {
    // using vector math; minimal allocs
    const v0 = this._tmpV0 || (this._tmpV0 = new THREE.Vector3());
    const v1 = this._tmpV1 || (this._tmpV1 = new THREE.Vector3());
    const v2 = this._tmpV2 || (this._tmpV2 = new THREE.Vector3());

    v0.copy(B).sub(A);
    v1.copy(C).sub(A);
    v2.copy(P).sub(A);

    const d00 = v0.dot(v0);
    const d01 = v0.dot(v1);
    const d11 = v1.dot(v1);
    const d20 = v2.dot(v0);
    const d21 = v2.dot(v1);

    const denom = d00 * d11 - d01 * d01;
    if (!denom) return null;

    const v = (d11 * d20 - d01 * d21) / denom;
    const w = (d00 * d21 - d01 * d20) / denom;
    const u = 1 - v - w;

    return { u, v, w };
  }

  // Given a THREE.Raycaster hit, return twin UV pixel coords for all twin faces
  _getTwinUVPixelsFromHit(hitObj, mesh) {
    const twins = this._seamTwinCache?.twins;
    const uv = this._seamTwinCache?.uv;
    const pos = this._seamTwinCache?.pos;
    if (!twins || !uv || !pos) return [];

    const faceIndex = hitObj?.faceIndex;
    if (typeof faceIndex !== "number") return [];

    const twinFaces = twins[faceIndex];
    if (!twinFaces || !twinFaces.length) return [];

    // intersection point in LOCAL space
    const P = this._tmpP || (this._tmpP = new THREE.Vector3());
    P.copy(hitObj.point);
    mesh.worldToLocal(P);

    // barycentric on the original face
    const [i0, i1, i2] = this._getFaceVertexIndices(faceIndex) || [];
    if (i0 == null) return [];

    const A = this._tmpA || (this._tmpA = new THREE.Vector3());
    const B = this._tmpB || (this._tmpB = new THREE.Vector3());
    const C = this._tmpC || (this._tmpC = new THREE.Vector3());
    A.fromBufferAttribute(pos, i0);
    B.fromBufferAttribute(pos, i1);
    C.fromBufferAttribute(pos, i2);

    const bc = this._barycentric(P, A, B, C);
    if (!bc) return [];

    const out = [];

    for (const tf of twinFaces) {
      const inds = this._getFaceVertexIndices(tf);
      if (!inds) continue;

      const t0 = inds[0],
        t1 = inds[1],
        t2 = inds[2];

      const uv0 = { x: uv.getX(t0), y: uv.getY(t0) };
      const uv1 = { x: uv.getX(t1), y: uv.getY(t1) };
      const uv2 = { x: uv.getX(t2), y: uv.getY(t2) };

      // barycentric interpolate UV
      let u = bc.u * uv0.x + bc.v * uv1.x + bc.w * uv2.x;
      let v = bc.u * uv0.y + bc.v * uv1.y + bc.w * uv2.y;

      // match your FLIP_V handling
      if (this.FLIP_V) v = 1 - v;

      // apply uvRect + calib just like _intersectUV does
      const { x: rx, y: ry, w: rw, h: rh } = this.uvRect;
      let uRel = (u - rx) / rw;
      let vRel = (v - ry) / rh;
      uRel = uRel * this.calib.uScale + this.calib.uBias;
      vRel = vRel * this.calib.vScale + this.calib.vBias;

      if (uRel < 0 || uRel > 1 || vRel < 0 || vRel > 1) continue;

      out.push({
        u: uRel,
        v: vRel,
        x: uRel * this.size,
        y: vRel * this.size,
      });
    }

    return out;
  }

  _strokeTo(px, py, uvNow = null) {
    const last = this._lastHit;
    if (!last) return;

    const now = uvNow || { u: px / this.size, v: py / this.size, x: px, y: py };
    const W = this.size;

    const du = now.u - last.u;
    const dv = now.v - last.v;

    const dx = now.x - last.x,
      dy = now.y - last.y;
    const dist = Math.hypot(dx, dy);

    // NEW: if UV jumps hard (non-wrap seam), don't attempt a line segment
    const brushUV = this.brush / this.size; // brush size in UV units
    const UV_JUMP = Math.max(0.06, Math.min(0.16, brushUV * 6.0));
    // ^ tune multiplier: 5..8 is usually good
    const UV_JUMP_U = 0.08;
    const UV_JUMP_V = 0.12;
    if (Math.abs(du) > UV_JUMP || Math.abs(dv) > UV_JUMP) {
      this._dab(now.x, now.y);
      this._lastHit = { ...now };
      this._commitStrokeTick(true);
      return;
    }

    // allow longer segments now that mesh is locked; still protects insane teleports
    const MAX_JUMP = this.size * 0.35;

    // seam detection: UV teleport but small 3D move
    const lastHitObj = last?.hit;
    const nowHitObj = now?.hit;

    const lastWorld = lastHitObj?.point || null;
    const nowWorld = nowHitObj?.point || null;

    // uv jump in pixels
    const uvJumpPx = Math.hypot(now.x - last.x, now.y - last.y);

    // world move (meters-ish)
    let worldMove = 999;
    if (lastWorld && nowWorld) worldMove = lastWorld.distanceTo(nowWorld);

    // tuning
    const SEAM_WORLD_MAX = 0.06; // ~6cm (adjust if needed)
    const SEAM_UV_JUMP_MIN = this.size * 0.18; // big UV jump

    // If we teleported across UV islands but barely moved in 3D,
    // DO NOT draw a UV line. Stamp at both sides instead.
    if (worldMove < SEAM_WORLD_MAX && uvJumpPx > SEAM_UV_JUMP_MIN) {
      // stamp on the "now" UV
      this._dab(now.x, now.y);

      // stamp on twin UV island(s)
      const mesh = this._activeMesh;
      if (mesh && nowHitObj) {
        this._ensureSeamTwinsForMesh(mesh);
        const twins = this._getTwinUVPixelsFromHit(nowHitObj, mesh);
        for (const t of twins) this._dab(t.x, t.y);
      }

      this._lastRayHit = null;
      this._lastHit = { ...now };
      if (this.symmetry) {
        const mirNow = this._mirrorUV(now);
        this._lastHitMirror = { ...mirNow };
      }
      this._commitStrokeTick(false);
      return;
    }

    // keep your existing MAX_JUMP protection
    if (dist > MAX_JUMP) {
      this._dab(now.x, now.y);
      this._lastHit = { ...now };
      this._commitStrokeTick(true);
      return;
    }

    if (Math.abs(du) > 0.5) {
      const crossingToRight = du > 0;
      const edgeU = crossingToRight ? 1 : 0;
      const t = (edgeU - last.u) / du;
      const vAtEdge = last.v + t * dv;

      this._lineSym(last.x, last.y, edgeU * this.size, vAtEdge * this.size);

      const uOpp = edgeU === 0 ? 1 : 0;
      this._lineSym(uOpp * this.size, vAtEdge * this.size, now.x, now.y);
      // ...
    } else {
      // FAST: draw one canvas stroke segment (much cheaper than stamping circles)
      const A = this._ropeFilter(last.x, last.y);
      const B = this._ropeFilter(now.x, now.y);
      this._lineSym(A.x, A.y, B.x, B.y);
    }
    // ...

    this._lastRayHit = null; // for "stick to same mesh" logic ONLY

    this._lastHit = { ...now };
    if (this.symmetry) {
      const mirNow = this._mirrorUV(now);
      this._lastHitMirror = { ...mirNow };
    }

    this._commitStrokeTick(false);
  }

  _commitStrokeTick(force = false) {
    this._dirty = true;

    // schedule at most 1 upload per animation frame
    this._wantUpload = true;

    const doUpload = () => {
      this._uploadRaf = 0;
      if (!this._wantUpload) return;
      this._wantUpload = false;

      if (this.texture) this.texture.needsUpdate = true;

      // ✅ tell R3F demand loop to render a frame
      try {
        window.dispatchEvent(new Event("guidepaint:stroke"));
      } catch {}
    };

    if (force) {
      doUpload();
      return;
    }

    if (!this._uploadRaf) {
      this._uploadRaf = requestAnimationFrame(doUpload);
    }
  }

  // --------------- handlers ---------------
  _onDown(e) {
    if (e.pointerType === "pen") this._penActive = true;

    // If not enabled yet, allow armed Pencil to flip it on right now
    if (!this.enabled) {
      if (e.pointerType === "pen" && Date.now() < this._padArmedUntil) {
        this._holdPaint = true;
        this._holdErase = false;
        this._ropePt = null;
        this._rawPt = null;
        this.setEnabled(true);
      } else {
        return;
      }
    }

    e.stopPropagation();
    e.preventDefault();

    this.canvasEl?.style?.setProperty("touch-action", "none");
    this.canvasEl?.dispatchEvent(
      new CustomEvent("guidepaint:drag-start", { bubbles: true })
    );

    this._updatePointerFromEvent(e);
    const p = this._intersectUV();
    if (!p) return;

    this._lastUV = null;
    this._lastHitPoint = null;

    // ✅ seed from the first hit of the stroke
    if (p.hit?.point) this._lastHitPoint = p.hit.point.clone();
    this._lastUV = { u: p.u, v: p.v };

    // 👇 LOCK THE MESH FOR THIS STROKE
    this._activeMesh = p.hit?.object || null;
    this._lastHitPoint = p.hit?.point ? p.hit.point.clone() : null;

    this._pushUndo();
    this.ctx.lineCap = "round";
    this.ctx.lineJoin = "round";
    this.ctx.lineWidth = this.brush;
    this.redoStack.length = 0;
    this._emitHistory();

    this._dragging = true;
    this._lastHit = { ...p };
    this._ropePt = null;
    this._rawPt = null;
    if (this.symmetry) {
      const pm = this._mirrorUV(p);
      this._lastHitMirror = { ...pm };
      this._dot(pm.x, pm.y);
    }
    this._dot(p.x, p.y);

    this._pendingHit = null;

    this._pendingClientX = 0;
    this._pendingClientY = 0;
  }
  _tickMove() {
    this._moveRaf = 0;
    if (!this._dragging) return;

    // Raycast ONCE per frame using the latest pointer coords
    this._updatePointerFromClientXY(this._pendingClientX, this._pendingClientY);

    let p = this._intersectUV(this._activeMesh);

    if (!p) {
      p = this._intersectUV(null);
      if (!p) return;
      this._activeMesh = p.hit?.object || null;
      this._ensureSeamTwinsForMesh(this._activeMesh);
    }

    this._strokeTo(p.x, p.y, p);

    // keep running while dragging (even if pointer events slow down)
    if (this._dragging && !this._moveRaf) {
      this._moveRaf = requestAnimationFrame(this._tickMove);
    }
  }

  _onMove(e) {
    if (!this.enabled || !this._dragging) return;
    e.stopPropagation();
    e.preventDefault();

    // Just store the latest screen coords; do NOT raycast here.
    this._pendingClientX = e.clientX;
    this._pendingClientY = e.clientY;

    if (!this._moveRaf) {
      this._moveRaf = requestAnimationFrame(this._tickMove);
    }
  }

  _onUp(e) {
    if (e?.pointerType === "pen") this._penActive = false;
    if (!this._dragging) {
      // even if not dragging, consider disabling if nothing is armed
      const stillArmed = Date.now() < this._padArmedUntil;
      if (
        this._autoDisable &&
        !this._holdPaint &&
        !this._holdErase &&
        !this._penActive &&
        !stillArmed
      ) {
        this.setEnabled(false);
      }
      this._activeMesh = null;
      return;
    }

    this._dragging = false;
    this._lastHitMirror = null;

    this._lastUV = null;
    this._lastHitPoint = null;

    if (this._moveRaf) {
      cancelAnimationFrame(this._moveRaf);
      this._moveRaf = 0;
    }
    this._pendingHit = null;
    this._commitStrokeTick(true); // final upload

    this.canvasEl?.dispatchEvent(
      new CustomEvent("guidepaint:drag-end", { bubbles: true })
    );
    this._emitHistory();
    this._ropePt = null;
    this._rawPt = null;

    // ✅ properly check the arm window here
    const stillArmed = Date.now() < this._padArmedUntil;
    if (
      this._autoDisable &&
      !this._holdPaint &&
      !this._holdErase &&
      !this._penActive &&
      !stillArmed
    ) {
      this.setEnabled(false);
    }
  }

  _onKey(e) {
    const t = e.target;
    const tag = (t?.tagName || "").toLowerCase();
    const isTyping =
      tag === "input" || tag === "textarea" || t?.isContentEditable;

    const key = (e.key || "").toLowerCase();

    // Don’t steal keys while typing in inputs/textareas
    if (isTyping) return;

    // Brush size: [ and ] to adjust
    if (key === "]" && !e.metaKey && !e.ctrlKey) {
      this.setBrush(this.brush + 2);
      e.preventDefault();
      return;
    }
    if (key === "[" && !e.metaKey && !e.ctrlKey) {
      this.setBrush(this.brush - 2);
      e.preventDefault();
      return;
    }

    // Undo
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && key === "z") {
      e.preventDefault();
      this.undo();
      return;
    }
    // Redo (Cmd/Ctrl + Shift + Z or Cmd/Ctrl + Y)
    if (
      (e.metaKey || e.ctrlKey) &&
      ((e.shiftKey && key === "z") || key === "y")
    ) {
      e.preventDefault();
      this.redo();
      return;
    }

    // HOLD-TO-PAINT (D)
    if (key === "d") {
      if (e.type === "keydown" && !e.repeat) {
        this._autoDisable = true; // auto-disable at stroke end
        this._holdPaint = true;
        this._holdErase = false;
        this.setEnabled(true);
      } else if (e.type === "keyup") {
        this._holdPaint = false;
        this.setEnabled(false); // stop on release
      }
      e.preventDefault();
      return;
    }

    // HOLD-TO-ERASE (E)
    if (key === "e" && !e.metaKey && !e.ctrlKey) {
      if (e.type === "keydown" && !e.repeat) {
        this._holdErase = true;
        this._holdPaint = false;
        this.setEnabled(true);
      } else if (e.type === "keyup") {
        this._holdErase = false;
        const stillArmed = Date.now() < this._padArmedUntil;
        if (
          !this._holdPaint &&
          !this._penActive &&
          !this._dragging &&
          !stillArmed
        ) {
          this.setEnabled(false);
        }
      }
      e.preventDefault();
      return;
    }
  }
}
